import {
  ANCHOR_DIRECTION_PROPERTY,
  ANCHOR_DOWNWARD,
  ANCHOR_LEFT_PROPERTY,
  ANCHOR_OFFSET_PROPERTY,
  ANCHOR_TOP_PROPERTY,
  ANCHOR_UPWARD,
} from '../../../utils/spds/constants/popover';
import { Metadata } from '../../../utils/spds/metadata';
import { TOP } from '../../../utils/spds/constants/positioning';
import {
  calculatePosition,
  clampToViewport,
  parsePlacement,
  resolveAnchoredPosition,
} from '../../../utils/spds/positioning';
import { registerDestructor } from '@ember/destroyable';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import Super from './base.gjs';
import styles from './popover.module.scss';

const POPOVER_OPEN = ':popover-open';

/** How far an anchor may drift before it counts as having moved, in px. */
const RECT_TOLERANCE = 0.5;

/** The edges compared to decide whether an anchor has moved. */
const RECT_EDGES = ['height', 'left', 'top', 'width'];

/** Stands in for the element's own state where the API is missing. */
const openPopovers = new WeakSet();

let apiSupported;

/**
 * Whether two rects describe the same box, to within a subpixel.
 *
 * @param {DOMRect} [rect]
 * @param {DOMRect} [other]
 * @returns {boolean}
 */
function rectsMatch(rect, other) {
  if (!rect || !other) return false;

  return RECT_EDGES.every(edge => Math.abs(rect[edge] - other[edge]) < RECT_TOLERANCE);
}

/**
 * Whether this browser implements the Popover API. Answered once — nothing about it changes over a
 * page's life — and false in FastBoot, where there is no element to ask.
 *
 * @returns {boolean}
 */
function supportsPopoverApi() {
  apiSupported ??=
    typeof HTMLElement !== 'undefined' && typeof HTMLElement.prototype.showPopover === 'function';

  return apiSupported;
}

/**
 * Whether `element` is showing as a popover, asked in a way that survives a browser without the
 * pseudo-class, where `matches(':popover-open')` is a `SyntaxError` rather than a false match.
 *
 * @param {HTMLElement} [element]
 * @returns {boolean}
 */
export function isElementPopoverOpen(element) {
  if (!element?.matches) return false;
  if (!supportsPopoverApi()) return openPopovers.has(element);

  return element.matches(POPOVER_OPEN);
}

const METADATA = Metadata.define({
  description: `
The SPDS base class for surfaces that render in the browser's top layer through the
[Popover API](https://developer.mozilla.org/en-US/docs/Web/API/Popover_API): menus, tooltips, and
toast messages. It houses their common behavior, but does not provide a template, and it declares
no arguments of its own — each surface keeps its own argument names and maps them onto the hooks
below.

### Opening and closing

\`showPopover\` and \`hidePopover\` wrap the native calls with the checks every surface needs, and
report whether the element actually changed state, which is what an exit animation has to be
sequenced off. A missing, disconnected, or already-torn-down element is a no-op rather than a
throw: the native \`showPopover()\` rejects a disconnected element, and either call rejects an
element whose \`popover\` attribute has gone away.

No surface hangs its *visibility* on any of this. Each one shows and hides from a class it renders
for itself, and the popover is what puts that surface in the top layer. Keeping the two separate is
what lets the same CSS work in a browser predating the API — where \`:popover-open\` is not a false
match but a parse error that discards the whole rule — and what lets Percy screenshot an open
surface, since its serialized DOM has no popover state at all. Where the API is missing the calls
stand down and the state is tracked here instead, so \`isPopoverOpen\` reads the same either way.
What is lost there is the top layer; see \`styles/addon.scss\`.

### Anchored positioning

A top-layer surface is positioned against the viewport rather than against a parent, so anchoring
is measurement rather than layout. \`positionPopover\` measures the anchor, resolves a placement
that fits the viewport, and hands the result to \`publishPosition\`, which writes it to the
element. The default publishes the anchor's own edge as custom properties
(\`--spds-popover-anchor-top\`, \`--spds-popover-anchor-left\`, \`--spds-popover-anchor-offset\`)
and lets the stylesheet open the gap from there, which keeps the offset a CSS length the browser
resolves. A surface that needs its position written some other way overrides the hook.

Anchors move, so \`startTrackingAnchor\` follows one for as long as the surface is showing,
on a frame loop that repositions when the anchor's rect has changed and when scroll or resize asks
it to. The rect comparison is what catches the moves the browser does not announce — a transform
settling, an ancestor animating, content arriving above the anchor. Surfaces that dismiss on scroll
instead never start.

However the position is resolved, it is confined to the viewport before it is written. Placement
correction can only be as right as the rect it was handed, and a surface positioned against a stale
one would otherwise paint out of sight, which reads as a surface that never opened.

### Hooks

* \`anchor\`, \`anchorDistance\`, \`anchorOffset\`, \`requestedPlacement\` — what to measure against,
  the gap as pixels and as an authored length, and the placement to prefer. Each reads the
  subclass's own arguments.
* \`anchored\` — whether the shared placement CSS applies. Opt in when \`publishPosition\` is left
  to the default.
* \`prepareForMeasurement\` — runs against the anchor rect before the surface measures itself, for
  a subclass whose own size depends on the anchor's.
* \`publishPosition\` — writes the resolved geometry to the element.
  `,
  name: 'Popover',
});

/**
 * Base class for SPDS top-layer surfaces.
 *
 * @property {boolean} anchored whether the shared anchored placement CSS applies
 */
export default class SpdsCorePopover extends Super {
  static metadata = this.setMetadata(METADATA, import.meta.url);

  baseStyles = styles;

  #anchorFrame = null;
  #positionedAgainst = null;
  #repositionRequested = false;
  #trackingAnchor = false;

  @service fastboot;

  @tracked resolvedPlacement = null;

  get classesToMap() {
    return ['anchored', 'animated'];
  }

  /**
   * The element the surface positions itself against. Every SPDS surface that anchors takes it as
   * `@anchorElement`; the ones that do not simply never have one.
   */
  get anchor() {
    return this.args.anchorElement;
  }

  get anchorBoundingRect() {
    return this.anchor?.getBoundingClientRect();
  }

  /**
   * The gap between anchor and surface, in pixels. The stylesheet opens the gap for a surface that
   * publishes it as a length, but the placement math still has to know how big it is: whether the
   * surface fits below its anchor depends on it.
   */
  get anchorDistance() {
    return 0;
  }

  /** The same gap as an authored CSS length, for the stylesheet to resolve. */
  get anchorOffset() {
    return '0px';
  }

  /**
   * Whether the shared placement rules apply, which is to say whether `publishPosition` is left to
   * the default. Opting out keeps a surface that positions itself some other way out of a
   * specificity race with them.
   */
  get anchored() {
    return false;
  }

  get animated() {
    return this.argOrDefault('animated');
  }

  /**
   * The placement the surface is rendered at: what the viewport allowed once it could be measured,
   * falling back to what was asked for until there is something to measure.
   */
  get activePlacement() {
    return this.resolvedPlacement ?? this.requestedPlacement;
  }

  get canBePositioned() {
    return this.hasAnchor && !!this.baseElement && !this.fastboot?.isFastBoot;
  }

  get hasAnchor() {
    return !!this.anchor;
  }

  /** Whether the element is currently showing as a popover. */
  get isPopoverOpen() {
    return isElementPopoverOpen(this.baseElement);
  }

  /** The placement asked for, before any viewport correction. */
  get requestedPlacement() {
    return this.argOrDefault('placement');
  }

  /** Closes the popover if it is currently showing. */
  hidePopover() {
    let element = this.baseElement;

    if (!element?.isConnected || !isElementPopoverOpen(element)) return false;

    if (!supportsPopoverApi()) {
      openPopovers.delete(element);

      return true;
    }

    try {
      element.hidePopover();

      return true;
    } catch {
      // The node was disconnected, or the browser closed the popover, between the check and here.
      return false;
    }
  }

  /**
   * Measures the anchor, resolves a placement that fits the viewport, and publishes the result.
   * Must run while the surface is showing so the measured rect reflects its final size.
   *
   * @param {DOMRect} [anchorRect] - a rect already measured this frame, so following the anchor
   *   costs one measurement rather than two
   */
  positionPopover(anchorRect = this.anchorBoundingRect) {
    if (!this.canBePositioned) return;

    this.#positionedAgainst = anchorRect;

    this.prepareForMeasurement(anchorRect);

    let floatingRect = this.baseElement.getBoundingClientRect();
    let { top, left, placement } = resolveAnchoredPosition({
      anchorRect,
      floatingRect,
      offset: this.anchorDistance,
      placement: this.requestedPlacement,
    });

    this.publishPosition({ anchorRect, floatingRect, left, placement, top });
  }

  /**
   * Hook for a surface whose own size depends on its anchor's, so that the rect handed to the
   * placement math reflects the final size.
   */
  prepareForMeasurement() {}

  /**
   * Publishes the side of the anchor the surface opens on, as the multiplier the stylesheet applies
   * to the gap and to the distance the entry animation travels.
   *
   * It travels as a property rather than through a class because both of those are timing-sensitive
   * and `classesToMap` is a render away. `@starting-style` captures the entry animation on the
   * first style resolution after the surface opens, and the exit reads the same declarations as it
   * leaves — a class arriving late would animate the wrong way, and one released early would
   * reverse the exit mid-fade. An inline property is written in whatever order the component needs.
   *
   * @param {string} placement
   */
  publishDirection(placement) {
    let flipped = parsePlacement(placement)[0] === TOP;

    this.baseElement.style.setProperty(
      ANCHOR_DIRECTION_PROPERTY,
      flipped ? ANCHOR_UPWARD : ANCHOR_DOWNWARD
    );
  }

  /**
   * Writes the resolved geometry to the element. The default publishes coordinates flush against
   * the anchor and leaves the gap to the stylesheet, which is what keeps the offset a CSS length
   * the browser resolves rather than one this class has to understand.
   *
   * @param {object} position
   * @param {DOMRect} position.anchorRect
   * @param {DOMRect} position.floatingRect
   * @param {string} position.placement - the placement the viewport allowed
   */
  publishPosition({ anchorRect, floatingRect, placement }) {
    let { top, left } = calculatePosition({ anchorRect, floatingRect, offset: 0, placement });
    let { style } = this.baseElement;

    // What the stylesheet paints is this edge plus the gap, so that is what has to be held on
    // screen. The gap is put back afterwards to keep the published value the anchor's own edge.
    let gap = this.#anchorGap(placement);
    let onscreen = clampToViewport({ top: top + gap, left, floatingRect });

    style.setProperty(ANCHOR_TOP_PROPERTY, `${onscreen.top - gap}px`);
    style.setProperty(ANCHOR_LEFT_PROPERTY, `${onscreen.left}px`);
    style.setProperty(ANCHOR_OFFSET_PROPERTY, this.anchorOffset);

    this.publishDirection(placement);

    this.resolvedPlacement = placement;
  }

  setup() {
    registerDestructor(this, this.stopTrackingAnchor);
  }

  /**
   * The gap as the stylesheet applies it: subtracted from the published edge for a surface that
   * opens upward, added for one that opens down.
   *
   * @param {string} placement
   * @returns {number}
   */
  #anchorGap(placement) {
    let { anchorDistance } = this;

    return parsePlacement(placement)[0] === TOP ? -anchorDistance : anchorDistance;
  }

  /** Opens the popover unless it is already showing. */
  showPopover() {
    let element = this.baseElement;

    if (!element?.isConnected || isElementPopoverOpen(element)) return false;

    if (!supportsPopoverApi()) {
      openPopovers.add(element);

      return true;
    }

    try {
      element.showPopover();

      return true;
    } catch {
      // The node was disconnected between the check and here; there is nothing left to show.
      return false;
    }
  }

  /**
   * A top-layer surface is positioned against the viewport, so it no longer scrolls with the
   * content its anchor lives in. Follow the anchor for as long as the surface is showing.
   *
   * Scroll and resize are the moves the browser announces. They are not the only ones an anchor
   * makes: a transform settling, an ancestor animating, an image or a font arriving above it in the
   * document all move an anchor in silence, and each leaves the surface pointing at a place its
   * anchor has left. So a frame loop measures the anchor while the surface is open and repositions
   * whenever the rect has changed, which covers every kind of move, announced or not.
   *
   * The events feed that same loop rather than scheduling work of their own. A burst of them
   * collapses into one reposition, and the measurement the loop already took is reused, so
   * following an anchor costs a single rect read per frame however loudly the page is moving.
   */
  startTrackingAnchor() {
    if (this.#trackingAnchor || this.fastboot?.isFastBoot) return;

    this.#trackingAnchor = true;
    window.addEventListener('scroll', this.scheduleReposition, { capture: true, passive: true });
    window.addEventListener('resize', this.scheduleReposition, { passive: true });
    this.#followAnchor();
  }

  #followAnchor = () => {
    if (!this.#trackingAnchor) return;

    let anchorRect = this.anchorBoundingRect;

    // An anchor with no rect to read has nothing to say about where the surface belongs, so the
    // last position stands rather than being recomputed from nothing.
    if (
      anchorRect &&
      (this.#repositionRequested || !rectsMatch(anchorRect, this.#positionedAgainst))
    ) {
      this.#repositionRequested = false;
      this.positionPopover(anchorRect);
    }

    this.#anchorFrame = requestAnimationFrame(this.#followAnchor);
  };

  /**
   * Asks the frame loop for a reposition on its next pass. A resize can change which placement fits
   * without moving the anchor at all, which is the case the rect comparison cannot see.
   */
  scheduleReposition = () => {
    this.#repositionRequested = true;
  };

  stopTrackingAnchor = () => {
    if (this.#anchorFrame) {
      cancelAnimationFrame(this.#anchorFrame);
      this.#anchorFrame = null;
    }

    this.#repositionRequested = false;

    if (!this.#trackingAnchor) return;

    this.#trackingAnchor = false;
    window.removeEventListener('scroll', this.scheduleReposition, { capture: true });
    window.removeEventListener('resize', this.scheduleReposition);
  };
}

export const CORE_POPOVER_METADATA = SpdsCorePopover.metadata;
